import { apiClient } from '@/common/lib';

/**
 * Safely download a media file directly to user's disk without redirecting or opening in a new tab.
 *
 * @param {string} mediaUrl - The media URL (can be absolute or relative)
 * @param {string} [preferredFileName] - The desired filename
 */
export async function downloadMediaFile(mediaUrl, preferredFileName) {
  if (!mediaUrl) return;

  // Extract a sensible filename if none provided or generic
  let fileName = preferredFileName;
  if (
    !fileName ||
    fileName === 'download' ||
    fileName === 'Attachment' ||
    fileName === 'Document'
  ) {
    try {
      const urlObj = new URL(mediaUrl, window.location.href);
      const segments = urlObj.pathname.split('/');
      const last = segments[segments.length - 1];
      if (last && last.includes('.')) {
        fileName = decodeURIComponent(last);
      }
    } catch {
      // fallback
    }
  }
  if (!fileName) fileName = 'download';

  // In development / local environment, convert absolute remote media URLs pointing to /media/
  // into same-origin relative URLs (/media/...) so Vite dev proxy forwards them without CORS restrictions.
  let fetchUrl = mediaUrl;
  if (
    (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) &&
    mediaUrl.includes('/media/')
  ) {
    try {
      const parsed = new URL(mediaUrl);
      fetchUrl = parsed.pathname + parsed.search;
    } catch {
      fetchUrl = mediaUrl;
    }
  }

  let blob = null;

  // Strategy 1: Direct fetch (works for relative / proxied / same-origin and CORS-enabled URLs)
  try {
    const res = await fetch(fetchUrl);
    if (res.ok) {
      blob = await res.blob();
    }
  } catch {
    // continue to fallback
  }

  // Strategy 2: apiClient (Axios with auth headers)
  if (!blob) {
    try {
      const response = await apiClient.get(fetchUrl, { responseType: 'blob' });
      blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
    } catch {
      // continue to fallback
    }
  }

  // Strategy 3: Canvas extraction for images if direct blob failed
  if (
    !blob &&
    (fetchUrl.match(/\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i) ||
      mediaUrl.match(/\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i))
  ) {
    try {
      blob = await new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            canvas.toBlob((b) =>
              b ? resolve(b) : reject(new Error('Canvas conversion failed')),
            );
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = reject;
        img.src = fetchUrl;
      });
    } catch {
      // ignore
    }
  }

  // If blob was successfully acquired, trigger instant direct download to disk
  if (blob) {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    return;
  }

  // Final fallback without target="_blank" so it does not open a new tab
  const link = document.createElement('a');
  link.href = fetchUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
